export type TopicFormField = {
  key: string
  label: string
  type: 'text' | 'textarea' | 'money' | 'number' | 'datetime' | 'radio' | 'checkbox'
  required?: boolean
  options?: {value: string; label: string}[]
  help?: string
  maxLength?: number
}

export type TopicFormDefinition = {
  topicId: string
  requiresPromotions: boolean
  requiresOriginalList?: boolean
  hasSegmentationSelector?: boolean
  requiresFamilies?: boolean
  disallowSuggestedList?: boolean
  fields: TopicFormField[]
}

export type TopicFormInput = {
  promotionIds: string[]
  values: Record<string, unknown>
  details: string
  attachmentCount: number
}

const yesNoOptions = [
  {value: 'sim', label: 'Sim'},
  {value: 'nao', label: 'Não'},
]
const actionOptions = [
  {value: 'incluir', label: 'Incluir'},
  {value: 'excluir', label: 'Excluir'},
]

function yesNo(key: string, label: string): TopicFormField {
  return {key, label, type: 'radio', required: true, options: yesNoOptions}
}

function text(key: string, label: string, required = true, maxLength = 255): TopicFormField {
  return {key, label, type: 'text', required, maxLength}
}

function renamedPromotionFields(): TopicFormField[] {
  return [
    text('novoNomePromocao', 'Novo nome da promoção', false, 250),
    text('novaDescricaoPromocao', 'Nova descrição da promoção', false, 250),
  ]
}

function defineTopic(
  topicId: string,
  requiresPromotions: boolean,
  fields: TopicFormField[],
  flags: Pick<
    TopicFormDefinition,
    | 'requiresOriginalList'
    | 'hasSegmentationSelector'
    | 'requiresFamilies'
    | 'disallowSuggestedList'
  > = {}
): TopicFormDefinition {
  return {topicId, requiresPromotions, fields, ...flags}
}

export const topicForms: Record<string, TopicFormDefinition> = {
  'cancelamento-encerramento': defineTopic('cancelamento-encerramento', true, []),
  'cupom-visivel-carrinho': defineTopic('cupom-visivel-carrinho', true, [
    yesNo('cupomVisivelCarrinho', 'Exibir cupom no carrinho?'),
  ]),
  reativacao: defineTopic('reativacao', true, [
    {key: 'novaDataFim', label: 'Nova data fim', type: 'datetime', required: true},
  ]),
  'alteracao-budget': defineTopic('alteracao-budget', false, [
    yesNo('budgetAtrelado', 'Budget atrelado?'),
    {key: 'novoBudgetTotal', label: 'Novo valor TOTAL do budget', type: 'money', required: true},
    yesNo('ativarTodasPromocoes', 'Ativar todas as promoções?'),
  ]),
  'budget-atrelado': defineTopic('budget-atrelado', true, [
    text('nomeBudget', 'Nome do Budget (ID da promo)'),
    text('descricaoBudget', 'Descrição do budget (nome do card)'),
    {key: 'budgetTotal', label: 'Valor TOTAL do budget', type: 'money', required: true},
  ]),
  'alteracao-cnpj': defineTopic(
    'alteracao-cnpj',
    false,
    [
      {
        key: 'tipoAlteracaoCnpj',
        label: 'Alteração desejada',
        type: 'radio',
        required: true,
        options: [
          {value: 'adicionar', label: 'Adicionar cnpj em segmentação criada'},
          {value: 'substituir', label: 'Substituir base inteira de CNPJ'},
        ],
      },
    ],
    {hasSegmentationSelector: true}
  ),
  'alteracao-segmento': defineTopic('alteracao-segmento', true, [
    {
      key: 'tipoAlteracaoSegmento',
      label: 'Alteração de segmentação desejada',
      type: 'radio',
      required: true,
      options: [
        {value: 'excluir', label: 'Exclusão de segmentação da promoção'},
        {value: 'comportamento', label: 'Alterar comportamento de segmentação (include/exclude)'},
        {value: 'trocar', label: 'Troca de segmentação da promoção'},
      ],
    },
  ]),
  'alteracao-ean': defineTopic('alteracao-ean', false, [
    yesNo('familiaRecorrente', 'Família recorrente?'),
    {
      key: 'acoesEan',
      label: 'Ação alteração EAN',
      type: 'checkbox',
      required: true,
      options: actionOptions,
    },
  ]),
  'troca-familia': defineTopic(
    'troca-familia',
    true,
    [text('familiaAtual', 'ID/ Nome da família ATUAL')],
    {requiresFamilies: true, disallowSuggestedList: true}
  ),
  'alteracao-seller': defineTopic('alteracao-seller', true, [
    {
      key: 'acoesSeller',
      label: 'Ação alteração de seller',
      type: 'checkbox',
      required: true,
      options: actionOptions,
    },
  ]),
  'alteracao-vigencia': defineTopic('alteracao-vigencia', true, [
    {
      key: 'datasAlteradas',
      label: 'Qual data você gostaria de alterar?',
      type: 'checkbox',
      required: true,
      options: [
        {value: 'inicio', label: 'Data início'},
        {value: 'fim', label: 'Data fim'},
      ],
    },
  ]),
  'criacao-segmentacao-recorrente': defineTopic('criacao-segmentacao-recorrente', true, [
    text('nomeSegmentacao', 'Nome da segmentação'),
    yesNo('cnpjsEmAnexo', 'CNPJ estão em anexo?'),
  ]),
  'criacao-segmentacao-recompra': defineTopic(
    'criacao-segmentacao-recompra',
    true,
    [text('nomeSegmentacao', 'Nome da segmentação')],
    {requiresOriginalList: true}
  ),
  'criacao-familia': defineTopic('criacao-familia', true, [
    {
      ...text('nomeFamilia', 'Nome da família'),
      help: 'Não é permitido criar nome de família com acentuação, vírgula, nome único de Marca e Indústria.',
    },
    text('nomeExibicaoFamilia', 'Nome de exibição da família no site', false),
    {
      key: 'eansFamilia',
      label: 'EAN da família a serem incluídos',
      type: 'textarea',
      required: true,
      help: 'Para ser adicionados',
      maxLength: 20000,
    },
  ]),
  'alteracao-desconto': defineTopic(
    'alteracao-desconto',
    true,
    [
      {key: 'novoDesconto', label: 'Novo valor do desconto', type: 'number', required: true},
      ...renamedPromotionFields(),
    ],
    {disallowSuggestedList: true}
  ),
  'alteracao-valor-minimo-maximo': defineTopic(
    'alteracao-valor-minimo-maximo',
    true,
    [
      {
        key: 'valoresAlterados',
        label: 'Alterar o valor mínimo ou máximo?',
        type: 'checkbox',
        required: true,
        options: [
          {value: 'minimo', label: 'Valor mínimo'},
          {value: 'maximo', label: 'Valor máximo'},
        ],
      },
      ...renamedPromotionFields(),
    ],
    {disallowSuggestedList: true}
  ),
  'alteracao-quantidade-minima-maxima': defineTopic(
    'alteracao-quantidade-minima-maxima',
    true,
    [
      {
        key: 'escopoQuantidade',
        label: 'Alteração na promoção toda ou de uma família específica?',
        type: 'radio',
        required: true,
        options: [
          {value: 'promocao', label: 'Promoção toda'},
          {value: 'familia', label: 'Família específica'},
        ],
      },
      {
        key: 'quantidadesAlteradas',
        label: 'Alterar a quantidade mínima ou máxima?',
        type: 'checkbox',
        required: true,
        options: [
          {value: 'minima', label: 'Quantidade mínima'},
          {value: 'maxima', label: 'Quantidade máxima'},
        ],
      },
      ...renamedPromotionFields(),
    ],
    {disallowSuggestedList: true}
  ),
  'alteracao-outros-campos': defineTopic(
    'alteracao-outros-campos',
    true,
    [
      {
        ...text('camposAlterados', 'Quais campos?'),
        help: 'Campos não editáveis: canal, cumulatividade, budget ID e quantidade de uso por CNPJ.',
      },
    ],
    {disallowSuggestedList: true}
  ),
  'alteracao-icone-lista-sugerida': defineTopic('alteracao-icone-lista-sugerida', false, [
    {
      key: 'tipoSolicitacao',
      label: 'Tipo de solicitação',
      type: 'radio',
      options: [
        {value: 'criacao', label: 'Criação'},
        {value: 'alteracao', label: 'Alteração'},
      ],
    },
  ]),
}

export function getTopicForm(topicId: string): TopicFormDefinition | undefined {
  return Object.prototype.hasOwnProperty.call(topicForms, topicId) ? topicForms[topicId] : undefined
}

/** Currency permits Brazilian grouping; plain numbers permit only a decimal separator. */
export function parseTopicNumber(value: unknown, money = false): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (typeof value !== 'string') return undefined
  const normalized = money ? value.trim().replace(/^R\$\s*/, '') : value.trim()
  if (!normalized) return undefined
  let decimal: string
  if (money && /^-?\d{1,3}(?:\.\d{3})+(?:,\d+)?$/.test(normalized)) {
    decimal = normalized.replace(/\./g, '').replace(',', '.')
  } else if (/^-?\d+(?:[.,]\d+)?$/.test(normalized)) {
    decimal = normalized.replace(',', '.')
  } else {
    return undefined
  }
  const parsed = Number(decimal)
  return Number.isFinite(parsed) ? parsed : undefined
}

function isValidDateTime(value: unknown): boolean {
  if (typeof value !== 'string') return false
  const iso = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value)
  const brazilian = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/.exec(value)
  const parts = iso
    ? [iso[1], iso[2], iso[3], iso[4], iso[5], iso[6] || '0'].map(Number)
    : brazilian
    ? [brazilian[3], brazilian[2], brazilian[1], brazilian[4], brazilian[5], '0'].map(Number)
    : undefined
  if (!parts) return false
  const [year, month, day, hour, minute, second] = parts
  if (month < 1 || month > 12 || day < 1 || hour > 23 || minute > 59 || second > 59) {
    return false
  }
  const date = new Date(0)
  date.setUTCFullYear(year, month - 1, day)
  date.setUTCHours(hour, minute, second, 0)
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  )
}

function isEmpty(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && !value.trim()) ||
    (Array.isArray(value) && value.length === 0)
  )
}

function isTopicReference(value: unknown, segmentOnly = false): boolean {
  if (typeof value !== 'string') return false
  const segmentation =
    /^solicitacao:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
      value
    )
  const briefingId = /^briefing:([1-9]\d{0,19})$/.exec(value)?.[1]
  const validBriefing =
    !!briefingId && (briefingId.length < 20 || briefingId <= '18446744073709551615')
  return segmentation || (!segmentOnly && validBriefing)
}

export function validateTopicForm(
  form: TopicFormDefinition,
  input: TopicFormInput
): Record<string, string> {
  const errors: Record<string, string> = {}
  const values = input.values || {}
  if (form.requiresPromotions && !input.promotionIds?.length) {
    errors.promotionIds = 'Selecione ao menos uma promoção.'
  }
  if (
    form.requiresOriginalList &&
    (typeof values.listaOriginalId !== 'string' || !values.listaOriginalId.trim())
  ) {
    errors.listaOriginalId = 'Selecione a lista original.'
  }
  if (form.hasSegmentationSelector) {
    const segmentationId = values.segmentacaoId
    const omitted =
      segmentationId === null ||
      segmentationId === undefined ||
      (typeof segmentationId === 'string' && !segmentationId.trim())
    if (!omitted && !isTopicReference(segmentationId, true)) {
      errors.segmentacaoId = 'Selecione uma segmentação válida.'
    }
  }
  if (form.requiresFamilies) {
    const familyIds = values.familiaIds
    if (!Array.isArray(familyIds) || familyIds.length === 0) {
      errors.familiaIds = 'Selecione ao menos uma família.'
    } else if (familyIds.length > 100) {
      errors.familiaIds = 'Selecione no máximo 100 famílias.'
    } else if (familyIds.some((id) => !isTopicReference(id))) {
      errors.familiaIds = 'Selecione apenas famílias válidas.'
    } else if (new Set(familyIds.map((id) => String(id).toLowerCase())).size !== familyIds.length) {
      errors.familiaIds = 'Selecione cada família apenas uma vez.'
    }
  }
  if (typeof input.details === 'string' && input.details.length > 20000) {
    errors.details = 'O detalhamento deve ter no máximo 20000 caracteres.'
  }

  for (const field of form.fields) {
    const value = values[field.key]
    if (isEmpty(value)) {
      if (field.required) errors[field.key] = 'Campo obrigatório.'
      continue
    }
    if (field.type === 'radio') {
      if (typeof value !== 'string' || !field.options?.some((option) => option.value === value)) {
        errors[field.key] = 'Selecione uma opção válida.'
      }
    } else if (field.type === 'checkbox') {
      if (
        !Array.isArray(value) ||
        value.some(
          (choice) =>
            typeof choice !== 'string' || !field.options?.some((option) => option.value === choice)
        )
      ) {
        errors[field.key] = 'Selecione apenas opções válidas.'
      }
    } else if (field.type === 'money' || field.type === 'number') {
      const numericValue = parseTopicNumber(value, field.type === 'money')
      if (numericValue === undefined || numericValue < 0) {
        errors[field.key] = 'Informe um valor numérico válido, igual ou maior que zero.'
      }
    } else if (field.type === 'datetime') {
      if (!isValidDateTime(value)) errors[field.key] = 'Informe uma data e hora válidas.'
    } else if (typeof value !== 'string') {
      errors[field.key] = 'Informe um texto válido.'
    } else if (field.maxLength && value.length > field.maxLength) {
      errors[field.key] = 'Informe no máximo ' + field.maxLength + ' caracteres.'
    } else if (field.key === 'nomeFamilia' && /[\u0300-\u036f,]/.test(value.normalize('NFD'))) {
      errors[field.key] = 'O nome da família não pode conter acentos ou vírgulas.'
    }
  }
  return errors
}
